<script setup lang="ts">
import { useFetch, useRoute, useRuntimeConfig } from '#app'
import { useTemplateRef, onMounted, ref } from 'vue'
import MobileMenu from './SubMenusNavbar/MobileMenu.vue'
import type { CmsSettings } from '~/layers/cms/app/types/cmsTypes.js'
import { useAuthStore } from '~/layers/cms/app/stores/authStore.js'
import SubMenuONas from './SubMenusNavbar/SubMenuONas.vue'
import { getCmsSettings } from '#imports'

const route = useRoute()

const wrapElem = useTemplateRef<HTMLElement>('wrapElem')
const isHigherThan76 = ref(false)

const check = () => {
  if (!wrapElem.value) return
  isHigherThan76.value = wrapElem.value.scrollHeight > 80
}

onMounted(() => {
  check()
  new ResizeObserver(check).observe(wrapElem.value!)
  window.addEventListener('resize', check)
})

const SubMenu1Open = ref(false)
const SubMenu2Open = ref(false)
const SubMenu3Open = ref(false)
const SubMenu4Open = ref(false)
const MobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  if (SubMenu1Open.value || SubMenu2Open.value || SubMenu3Open.value || SubMenu4Open.value) {
    MobileMenuOpen.value = false
  } else {
    MobileMenuOpen.value = !MobileMenuOpen.value
  }

  SubMenu1Open.value = false
  SubMenu2Open.value = false
  SubMenu3Open.value = false
  SubMenu4Open.value = false
}

const toggleMenu1 = () => {
  SubMenu1Open.value = !SubMenu1Open.value
  SubMenu2Open.value = false
  SubMenu3Open.value = false
  SubMenu4Open.value = false
  MobileMenuOpen.value = false
}

const toggleMenu2 = () => {
  SubMenu1Open.value = false
  SubMenu2Open.value = !SubMenu2Open.value
  SubMenu3Open.value = false
  SubMenu4Open.value = false
  MobileMenuOpen.value = false
}

const toggleMenu3 = () => {
  SubMenu1Open.value = false
  SubMenu2Open.value = false
  SubMenu3Open.value = !SubMenu3Open.value
  SubMenu4Open.value = false
  MobileMenuOpen.value = false
}

const toggleMenu4 = () => {
  SubMenu1Open.value = false
  SubMenu2Open.value = false
  SubMenu3Open.value = false
  SubMenu4Open.value = !SubMenu4Open.value
  MobileMenuOpen.value = false
}

const closeAllMenus = () => {
  SubMenu1Open.value = false
  SubMenu2Open.value = false
  SubMenu3Open.value = false
  SubMenu4Open.value = false
  MobileMenuOpen.value = false
}

const config = useRuntimeConfig()
const { data } = await useFetch<CmsSettings>(`${config.public.apiBase}/api/get-cms-settings`, {
  default: () => ({} as CmsSettings),
  transform: response => (response ?? {}) as CmsSettings,
  getCachedData: (key, nuxtApp) =>
    nuxtApp.isHydrating ? nuxtApp.payload.data[key] : undefined
})
const settings = ref<CmsSettings>(data.value)

const authStore = useAuthStore()
onMounted(async () => {
  if (authStore.authorized) {
    const res = await getCmsSettings()
    if (res.status_ok) {
      settings.value = res.data as CmsSettings
    }
  }
})
</script>

<template>
  <div class="flex flex-col max-w-480 m-auto w-full fixed z-900 top-0 pointer-events-none">
    <nav
      :class="SubMenu1Open || SubMenu2Open || SubMenu3Open || SubMenu4Open ? 'bg-background-1' : 'bg-white'"
      class="pointer-events-auto w-full flex flex-row gap-10 justify-between px-xl py-4 xl:py-8 items-center z-100 transform-gpu duration-200 ease-in-out"
    >
      <NuxtLink
        to="/"
        @click="closeAllMenus"
      >
        <NuxtImg
          :src="settings.logo"
          loading="eager"
          width="196"
          height="54"
          quality="100"
          alt="ostoja seniora"
          class="object-contain w-30 xl:w-49 xl:min-w-49"
        />
      </NuxtLink>
      <div
        ref="wrapElem"
        class="hidden 2xl:flex flex-wrap-reverse justify-end gap-y-4 gap-x-2.5 relative items-center w-fit"
      >
        <div
          v-if="isHigherThan76"
          class="absolute top-1/2 left-0 w-full h-px bg-Tertiary -translate-y-1/2 pointer-events-none"
        />
        <div
          class="px-4 py-2.5 rounded-full transform-gpu transition-all duration-200 hover:bg-background-2 cursor-pointer"
          :class="route.path.includes('prywatny-dom-opieki') ? 'bg-background-2' : ''"
          @click="toggleMenu1"
        >
          <span class="text-Tekst text-nowrap! text-Button">Prywatny dom opieki</span>
        </div>

        <div
          class="px-4 py-2.5 rounded-full transform-gpu transition-all duration-200 hover:bg-background-2 cursor-pointer"
          :class="route.path.includes('sprzedaz-mieszkan') ? 'bg-background-2' : ''"
          @click="toggleMenu2"
        >
          <span class="text-Tekst text-nowrap! text-Button">Sprzedaż mieszkań</span>
        </div>
        <div
          class="px-4 py-2.5 rounded-full transform-gpu transition-all duration-200 hover:bg-background-2 cursor-pointer"
          :class="route.path.includes('wynajem-mieszkan') ? 'bg-background-2' : ''"
          @click="toggleMenu3"
        >
          <span class="text-Tekst text-nowrap! text-Button">Wynajem mieszkań</span>
        </div>
        <CmsLink
          v-slot="{ link }"
          :cms-data-path="['global', 'component_data', 'navbar_link_4']"
          global
          @click="closeAllMenus"
        >
          <NuxtLink
            :to="link?.href"
            :target="link?.target"
            class="px-4 py-2.5 rounded-full transform-gpu transition-all duration-200 hover:bg-background-2 cursor-pointer text-Button text-Tekst text-nowrap! link-text"
            :class="route.path.includes('restauracja-i-bistro') ? 'bg-background-2' : ''"
          >
            <span class="link-text">{{ link?.link_text }}</span>
          </NuxtLink>
        </CmsLink>
        <div
          class="px-4 py-2.5 rounded-full transform-gpu transition-all duration-200 hover:bg-background-2 cursor-pointer"
          :class="route.path.includes('o-nas') ? 'bg-background-2' : ''"
          @click="toggleMenu4"
        >
          <span class="text-Tekst text-nowrap! text-Button">O nas</span>
        </div>
        <CmsLink
          v-slot="{ link }"
          :cms-data-path="['global', 'component_data', 'navbar_link_2']"
          global
          @click="closeAllMenus"
        >
          <NuxtLink
            :to="link?.href"
            :target="link?.target"
            class="px-4 py-2.5 rounded-full transform-gpu transition-all duration-200 hover:bg-background-2 cursor-pointer text-Button text-Tekst text-nowrap! link-text"
            :class="route.path.includes('blog') ? 'bg-background-2' : ''"
          >
            <span class="link-text">{{ link?.link_text }}</span>
          </NuxtLink>
        </CmsLink>
        <CmsLink
          v-slot="{ link }"
          :cms-data-path="['global', 'component_data', 'navbar_link_1']"
          global
          @click="closeAllMenus"
        >
          <NuxtLink
            :to="link?.href"
            :target="link?.target"
            class="px-4 py-2.5 rounded-full transform-gpu transition-all duration-200 hover:bg-background-2 cursor-pointer text-Button text-Tekst text-nowrap! link-text"
            :class="route.path.includes('kontakt') ? 'bg-background-2' : ''"
          >
            <span class="link-text">{{ link?.link_text }}</span>
          </NuxtLink>
        </CmsLink>
      </div>
      <div class="hidden 2xl:flex flex-row gap-8 items-center">
        <CmsLink
          v-slot="{ link }"
          :cms-data-path="['global', 'component_data', 'navbar_link_btn1']"
          global
        >
          <AppButton
            :href="link?.href"
            :target="link?.target"
            button-theme="Primary"
            @click="closeAllMenus"
          >
            <span class="text-nowrap! link-text">
              {{ link?.link_text ?? 'Lorem Ipsum' }}
            </span>
          </AppButton>
        </CmsLink>
        <CmsLink
          v-slot="{ link }"
          :cms-data-path="['global', 'component_data', 'navbar_link_btn2']"
          global
        >
          <AppButton
            :href="link?.href"
            :target="link?.target"
            button-theme="Secondary"
            @click="closeAllMenus"
          >
            <span class="text-nowrap! link-text">
              {{ link?.link_text ?? 'Lorem Ipsum' }}
            </span>
          </AppButton>
        </CmsLink>
      </div>
      <div
        class="2xl:hidden flex items-center"
        @click="toggleMobileMenu"
      >
        <i class="pi pi-bars text-Secondary text-4xl!" />
      </div>
    </nav>
    <div class="grid grid-cols-1 grid-rows-1 relative z-80 pointer-events-none">
      <SubMenusNavbarSubMenuPrywatnyDomOpieki
        :open="SubMenu1Open"
        @hide="toggleMenu1"
      />
      <SubMenusNavbarSubMenuSprzedazMieszkan
        :open="SubMenu2Open"
        @hide="toggleMenu2"
      />
      <SubMenusNavbarSubMenuWynajemMieszkan
        :open="SubMenu3Open"
        @hide="toggleMenu3"
      />
      <SubMenuONas
        :open="SubMenu4Open"
        @hide="toggleMenu4"
      />
      <MobileMenu
        :open="MobileMenuOpen"
        @hide="toggleMobileMenu"
        @show-menu1="toggleMenu1"
        @show-menu2="toggleMenu2"
        @show-menu3="toggleMenu3"
        @show-menu4="toggleMenu4"
      />
    </div>
  </div>
</template>
